import { useState, useEffect, useRef, useCallback, type MouseEvent as ReactMouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { ToyBrick, BookOpen, Clapperboard, Gamepad2, Play, Sparkles } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import './hover-slider-hero.css';

const STORAGE = 'https://hdtgqbtxmtvridkxsjcd.supabase.co/storage/v1/object/public/demo/Website';
const AVATARS = 'https://hdtgqbtxmtvridkxsjcd.supabase.co/storage/v1/object/public/demo/avatars';

type MascotKey = 'riko' | 'umi' | 'bibi' | 'yoyo';

const categories: Array<{
  key: MascotKey;
  label: string;
  labelKey: string;
  video: string;
  link: string;
  howHash: string;
  ChipIcon: typeof ToyBrick;
}> = [
  { key: 'riko', label: 'Toy',       labelKey: 'heroNav.toy',       video: `${STORAGE}/R_homepage.webm`, link: '/toy?new=true',       howHash: '#toy',       ChipIcon: ToyBrick },
  { key: 'umi',  label: 'Story',     labelKey: 'heroNav.story',     video: `${STORAGE}/U_homepage.webm`, link: '/story?new=true',     howHash: '#story',     ChipIcon: BookOpen },
  { key: 'bibi', label: 'Animation', labelKey: 'heroNav.animation', video: `${STORAGE}/B_homepage.webm`, link: '/animation?new=true', howHash: '#animation', ChipIcon: Clapperboard },
  { key: 'yoyo', label: 'Game',      labelKey: 'heroNav.game',      video: `${STORAGE}/Y_homepage.webm`, link: '/game',       howHash: '#game',      ChipIcon: Gamepad2 },
];

const RUBY_IMG = `${AVATARS}/RUBY.png`;
const secondaryCategories = [
  { label: 'Gallery',      labelKey: 'heroNav.gallery',     image: RUBY_IMG, link: '/gallery' },
  { label: 'How it works', labelKey: 'heroNav.howItWorks',  image: RUBY_IMG, link: '/how-it-works' },
  { label: 'Pricing',      labelKey: 'heroNav.pricing',     image: RUBY_IMG, link: '/pricing' },
  { label: 'Reviews',      labelKey: 'heroNav.reviews',     image: RUBY_IMG, link: '/reviews' },
];


// English fallback values for per-string fallback when Persian is missing
const EN_FALLBACK: Record<MascotKey, Record<string, string>> = {
  riko: { chipLabel: 'The toy maker', name: 'Riko', hookBefore: 'That wobbly monster on the fridge?', hookAfter: 'Riko makes it ', hookHighlight: 'real.', body: 'Send a drawing or a favourite photo — Riko tickles it into a huggable toy that arrives at your door.', ctaLabel: 'Make a friend with Riko', howLabel: 'How the magic works' },
  umi:  { chipLabel: 'The storyteller', name: 'Umi', hookBefore: 'Every picture is whispering a story.', hookAfter: 'Umi can ', hookHighlight: 'hear it.', body: "She'll spin it into a picture book with your child as the hero — their name shining on the cover.", ctaLabel: 'Open the book with Umi', howLabel: 'How the magic works' },
  bibi: { chipLabel: 'The animator', name: 'Bibi', hookBefore: 'Pictures get bored on the wall.', hookAfter: 'Bibi ', hookHighlight: 'wakes them up.', body: 'Send a doodle or a snapshot and watch it blink, giggle, and dance in its own mini movie.', ctaLabel: 'Wake it up with Bibi', howLabel: 'How the magic works' },
  yoyo: { chipLabel: 'The game maker', name: 'Yoyo', hookBefore: 'Yoyo never says "don\'t touch" —', hookAfter: 'Yoyo says ', hookHighlight: '"press start!"', body: 'Any drawing or photo becomes the star of its own little game — your child holds the controls.', ctaLabel: 'Press start with Yoyo', howLabel: 'How the magic works' },
};

export default function HoverSliderHero() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeSecondaryIndex, setActiveSecondaryIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const cursorRef = useRef<HTMLDivElement>(null);
  const cursor2Ref = useRef<HTMLDivElement>(null);
  const cursor3Ref = useRef<HTMLDivElement>(null);
  const [isHoverTarget, setIsHoverTarget] = useState(false);
  const { t } = useLanguage();

  // Translation helper with per-string EN fallback
  const tp = (mascot: MascotKey, field: string): string => {
    const key = `heroPanel.${mascot}.${field}` as any;
    const value = t(key);
    if (!value || value === key) return EN_FALLBACK[mascot][field] ?? '';
    return value;
  };

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    let raf: number;
    const pos = { x: 0, y: 0 };
    const smooth = { x: 0, y: 0 };
    const smooth2 = { x: 0, y: 0 };

    const onMove = (e: MouseEvent) => {
      pos.x = e.clientX;
      pos.y = e.clientY;
    };

    const animate = () => {
      smooth.x += (pos.x - smooth.x) * 0.15;
      smooth.y += (pos.y - smooth.y) * 0.15;
      smooth2.x += (pos.x - smooth2.x) * 0.08;
      smooth2.y += (pos.y - smooth2.y) * 0.08;

      if (cursorRef.current) { cursorRef.current.style.left = `${pos.x}px`; cursorRef.current.style.top = `${pos.y}px`; }
      if (cursor2Ref.current) { cursor2Ref.current.style.left = `${smooth.x}px`; cursor2Ref.current.style.top = `${smooth.y}px`; }
      if (cursor3Ref.current) { cursor3Ref.current.style.left = `${smooth2.x}px`; cursor3Ref.current.style.top = `${smooth2.y}px`; }

      if (root) {
        root.style.setProperty('--mouse-x', `${pos.x}px`);
        root.style.setProperty('--mouse-y', `${pos.y}px`);
      }

      raf = requestAnimationFrame(animate);
    };

    window.addEventListener('mousemove', onMove);
    raf = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('mousemove', onMove);
      cancelAnimationFrame(raf);
    };
  }, []);

  const handleMouseEnter = useCallback((index: number) => {
    setActiveIndex(index);
    setIsHoverTarget(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHoverTarget(false);
  }, []);

  /** First tap switches the mascot video; second tap (already active) navigates. */
  const handleCategoryActivate = useCallback(
    (index: number, event: ReactMouseEvent<HTMLAnchorElement>) => {
      if (index !== activeIndex) {
        event.preventDefault();
        setActiveIndex(index);
        setIsHoverTarget(true);
      }
    },
    [activeIndex],
  );

  const active = categories[activeIndex];
  const videoRefs = useRef<Array<HTMLVideoElement | null>>([]);

  useEffect(() => {
    videoRefs.current.forEach((video, i) => {
      if (!video) return;
      if (i === activeIndex) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }, [activeIndex]);

  return (
    <div className="hover-slider-root" ref={rootRef} data-active-mascot={active.key}>

      {/* Reveal background layers */}
      <div className="hero-reveal-layers">
        {categories.map((cat, i) => (
          <div
            key={cat.label}
            className={`hero-reveal-slide ${i === activeIndex ? 'show' : ''}`}
            data-mascot={cat.key}
          >
            <video
              ref={(el) => { videoRefs.current[i] = el; }}
              className="hero-layer-b"
              src={cat.video}
              autoPlay={i === activeIndex}
              muted
              loop
              playsInline
              preload={i === activeIndex ? 'auto' : 'metadata'}
            />
          </div>
        ))}
      </div>

      {/* 3-column grid */}
      <div className="hero-grid">
        {/* Left column – editorial text panel */}
        <div className="hero-left-col">
          <div className="hero-text-panel" key={active.key}>
            <div className="panel-scrim">
              <span className="hero-chip stagger-0">
                <active.ChipIcon size={16} strokeWidth={2.25} aria-hidden />
                <span>{tp(active.key, 'chipLabel')}</span>
              </span>

              <h2 className="hero-name stagger-1">
                {tp(active.key, 'name')}<span className="hero-name-dot">.</span>
              </h2>

              <p className="hero-hook stagger-2">
                {tp(active.key, 'hookBefore')}
                <br />
                {tp(active.key, 'hookAfter')}
                <span className="hero-hook-highlight">
                  {tp(active.key, 'hookHighlight')}
                  <svg className="squiggle" viewBox="0 0 300 12" preserveAspectRatio="none" aria-hidden>
                    <path
                      d="M2 8 Q 25 1, 50 6 T 100 6 T 150 6 T 200 6 T 250 6 T 298 6"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="4.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </p>

              <p className="hero-body stagger-3">{tp(active.key, 'body')}</p>

              <div className="hero-cta-row stagger-4">
                <Link to={active.link} className="hero-cta-primary">
                  <Play size={17} strokeWidth={2.5} fill="currentColor" aria-hidden />
                  <span>{tp(active.key, 'ctaLabel')}</span>
                </Link>
                <Link to={`/how-it-works${active.howHash}`} className="hero-cta-secondary">
                  <Sparkles size={17} strokeWidth={2} aria-hidden />
                  <span>{tp(active.key, 'howLabel')}</span>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Middle column – category links */}
        <div className="hero-middle-col">
          <ul className="slide-buttons" role="tablist" aria-label="Rubby products">
            {categories.map((cat, i) => (
              <li
                key={cat.key}
                className={i === activeIndex ? 'active' : ''}
                role="presentation"
                onMouseEnter={() => handleMouseEnter(i)}
                onMouseLeave={handleMouseLeave}
              >
                <Link
                  to={cat.link}
                  className="hover-target"
                  role="tab"
                  aria-selected={i === activeIndex}
                  onClick={(event) => handleCategoryActivate(i, event)}
                >
                  {t(cat.labelKey as any) || cat.label}
                </Link>
              </li>
            ))}
          </ul>

        </div>

        {/* Right column – empty */}
        <div className="hero-right-col" />

      </div>

      {/* Secondary category row */}
      <div className="hero-grid hero-grid-secondary">
        <div className="hero-left-col" />
        <div className="hero-middle-col">
          <ul className="slide-buttons">
            {secondaryCategories.map((cat, i) => (
              <li
                key={cat.link}
                className={i === activeSecondaryIndex ? 'active' : ''}
                onMouseEnter={() => { setActiveSecondaryIndex(i); setIsHoverTarget(true); }}
                onMouseLeave={handleMouseLeave}
              >
                <Link to={cat.link} className="hover-target">
                  {t(cat.labelKey as any) || cat.label}
                </Link>
              </li>
            ))}
          </ul>

        </div>
        <div className="hero-right-col">
          <div className="hero-image-stack">
            {secondaryCategories.map((cat, i) => (
              <Link
                key={cat.label}
                to={cat.link}
                className={`hero-image-item ${i === activeSecondaryIndex ? 'show' : ''}`}
              >
                <img src={cat.image} alt={cat.label} loading="lazy" />
              </Link>
            ))}
          </div>
        </div>
      </div>


      {/* Custom cursors */}
      <div ref={cursorRef} className="cursor" />
      <div ref={cursor2Ref} className={`cursor2 ${isHoverTarget ? 'hover-target' : ''}`} />
      <div ref={cursor3Ref} className="cursor3" />
    </div>
  );
}
